/**
 * Correcting the legajo itself: its name, its CUIT, who is on it, and putting it away.
 *
 * Everything here is the part of a case that a person typed, and people mistype. Without it the
 * only fix for a wrong CUIT was creating the legajo again and losing the work attached to it.
 */

import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, friendlyError } from "../../api/client";
import type { CaseOut } from "../../api/types";
import { useFocusTrap } from "../../lib/focus";
import { formatDateTime } from "../../lib/model";
import { Button, ErrorBanner } from "../../components/ui";

export default function CaseSettings({ kase, onClose }: { kase: CaseOut; onClose: () => void }) {
  const queryClient = useQueryClient();
  const dialog = useRef<HTMLDivElement>(null);
  useFocusTrap(dialog);
  const [legalName, setLegalName] = useState(kase.legal_name);
  const [cuit, setCuit] = useState(kase.cuit);
  const [notes, setNotes] = useState(kase.notes ?? "");
  const [assignee, setAssignee] = useState(kase.assigned_to ?? "");

  const users = useQuery({ queryKey: ["users"], queryFn: api.listUsers });

  async function refresh() {
    await queryClient.invalidateQueries({ queryKey: ["case", kase.id] });
    await queryClient.invalidateQueries({ queryKey: ["cases"] });
    await queryClient.invalidateQueries({ queryKey: ["activity", kase.id] });
    await queryClient.invalidateQueries({ queryKey: ["metrics"] });
  }

  const save = useMutation({
    mutationFn: async () => {
      await api.updateCase(kase.id, {
        legal_name: legalName.trim(),
        cuit: cuit.trim(),
        notes,
      });
      if ((assignee || null) !== (kase.assigned_to ?? null)) {
        await api.assignCase(kase.id, assignee || null);
      }
    },
    onSuccess: async () => {
      await refresh();
      onClose();
    },
  });

  const archive = useMutation({
    mutationFn: () => (kase.archived_at ? api.restoreCase(kase.id) : api.archiveCase(kase.id)),
    onSuccess: async () => {
      await refresh();
      onClose();
    },
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 px-6"
      onClick={onClose}
    >
      <div
        ref={dialog}
        role="dialog"
        aria-modal="true"
        aria-label="Datos del legajo"
        onKeyDown={(e) => e.key === "Escape" && onClose()}
        onClick={(e) => e.stopPropagation()}
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-6 shadow-xl"
      >
        <h2 className="text-lg font-semibold">Datos del legajo</h2>
        <p className="mt-1 text-sm text-muted">
          Corregir la razón social o el CUIT los cambia en todos los legajos de esa empresa.
        </p>

        <label className="mt-5 block text-sm font-medium" htmlFor="legal-name">
          Razón social
        </label>
        <input
          id="legal-name"
          value={legalName}
          onChange={(e) => setLegalName(e.target.value)}
          className="mt-1 w-full rounded-lg border border-line px-3 py-2 text-sm focus:border-primary focus:outline-none"
        />

        <label className="mt-4 block text-sm font-medium" htmlFor="cuit">
          CUIT
        </label>
        <input
          id="cuit"
          value={cuit}
          onChange={(e) => setCuit(e.target.value)}
          className="mt-1 w-full rounded-lg border border-line px-3 py-2 text-sm tabular-nums focus:border-primary focus:outline-none"
        />

        <label className="mt-4 block text-sm font-medium" htmlFor="assignee">
          A cargo
        </label>
        <select
          id="assignee"
          value={assignee}
          onChange={(e) => setAssignee(e.target.value)}
          className="mt-1 w-full rounded-lg border border-line px-3 py-2 text-sm focus:border-primary focus:outline-none"
        >
          <option value="">Sin asignar</option>
          {(users.data ?? []).map((user) => (
            <option key={user.id} value={user.id}>
              {user.name}
            </option>
          ))}
        </select>

        <label className="mt-4 block text-sm font-medium" htmlFor="case-notes">
          Notas del legajo <span className="font-normal text-muted">(opcional)</span>
        </label>
        <textarea
          id="case-notes"
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="mt-1 w-full rounded-lg border border-line px-3 py-2 text-sm focus:border-primary focus:outline-none"
        />

        {save.isError ?
          <div className="mt-4">
            <ErrorBanner {...friendlyError(save.error)} />
          </div>
        : null}

        <div className="mt-6 flex justify-end gap-2">
          <Button type="button" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" disabled={save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? "Guardando…" : "Guardar"}
          </Button>
        </div>

        <div className="mt-6 border-t border-line pt-4">
          <h3 className="text-sm font-medium">
            {kase.archived_at ? "Legajo archivado" : "Archivar el legajo"}
          </h3>
          <p className="mt-1 text-xs text-muted">
            {kase.archived_at ?
              `Archivado el ${formatDateTime(kase.archived_at)}. Sale de la cartera activa pero no se borra nada.`
            : "Sale de la cartera activa. No se borra nada y se puede restaurar cuando quieras."}
          </p>
          <Button className="mt-2" disabled={archive.isPending} onClick={() => archive.mutate()}>
            {kase.archived_at ? "Restaurar legajo" : "Archivar legajo"}
          </Button>
        </div>
      </div>
    </div>
  );
}
